import {
  getFeedbackQuestions,
  getSurveys,
  orEmptyArray,
} from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import { LoaderCircle, PlusIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import {
  PageBox,
  PageButtons,
  PageHeader,
  PageTitle,
} from '@/components/admin/page-box';
import { useSiteAccess } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import QuestionList from '@/features/survey/components/question-list';
import SurveyList from '@/features/survey/components/survey-list';

// Ports app/[brand]/admin/surveys/page.tsx. The Next server component awaited
// the surveys and feedback-questions lists; here both load via SWR and each
// mutation revalidates them via SWR `mutate` in place of `revalidatePath`.
// Surveys are super-admin only. Renders CONTENT only; the admin `<Shell>` wraps
// this route.
export function Surveys() {
  const t = useTranslations('admin');
  const { slug } = useBrand();
  const { access, loading: roleLoading } = useSiteAccess(slug);
  const isSuperAdmin = access?.superAdmin ?? false;

  const { data: surveys, mutate: mutateSurveys } = useSWR(
    isSuperAdmin ? ['admin/surveys'] : null,
    () => orEmptyArray(getSurveys()),
  );
  const { data: questions, mutate: mutateQuestions } = useSWR(
    isSuperAdmin ? ['admin/feedback-questions'] : null,
    () => orEmptyArray(getFeedbackQuestions()),
  );

  if (roleLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <PageBox>
        <PageHeader>
          <PageTitle>{t('surveys')}</PageTitle>
        </PageHeader>
        <p className="text-muted-foreground">{t('unauthorized')}</p>
      </PageBox>
    );
  }

  if (surveys === undefined || questions === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <PageBox>
        <PageHeader>
          <PageTitle>{t('surveys')}</PageTitle>
          <PageButtons>
            <Button asChild>
              <Link to={`/${slug}/admin/surveys/add`}>
                <PlusIcon className="size-4" />
                {t('add', { what: t('survey').toLowerCase() })}
              </Link>
            </Button>
          </PageButtons>
        </PageHeader>
        <SurveyList
          slug={slug}
          surveys={surveys}
          onChanged={() => mutateSurveys()}
        />
      </PageBox>
      <PageBox>
        <PageHeader>
          <PageTitle>{t('question')}</PageTitle>
          <PageButtons>
            <Button asChild>
              <Link to={`/${slug}/admin/surveys/addquestion`}>
                <PlusIcon className="size-4" />
                {t('add', { what: t('question').toLowerCase() })}
              </Link>
            </Button>
          </PageButtons>
        </PageHeader>
        <QuestionList
          slug={slug}
          questions={questions}
          onChanged={() => mutateQuestions()}
        />
      </PageBox>
    </>
  );
}
